/**
 * Schriftstück Senden - Tab-Seite (kein Dialog)
 * Erlaubt das Senden von E-Mails, Faxen und EBO aus einer Aufgabe heraus
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTabSearchParams } from '@/hooks/useTabRoute';
import {
  ArrowLeft, Send, Mail, Printer, Monitor
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Card, CardContent, CardHeader, CardTitle
} from '@/components/ui/card';
import { betreuungAPI } from '@/api/betreuungAPI';
import { toast } from 'sonner';

export default function SendSchriftstueckPage() {
  const navigate = useNavigate();
  const [searchParams] = useTabSearchParams();

  const aufgabeId = searchParams.get('aufgabeId') || '';
  const aufgabeTitel = searchParams.get('aufgabeTitel') || '';
  const klientId = searchParams.get('klientId') || '';
  const massnahmeBezeichnung = searchParams.get('massnahme') || '';

  const [kanal, setKanal] = useState('email');
  const [form, setForm] = useState({
    an: '', betreff: '', inhalt: '', fax_nummer: '', empfaenger_safe_id: ''
  });
  const [sending, setSending] = useState(false);

  const handleSend = async () => {
    setSending(true);
    try {
      const verknuepfungen = [];
      if (aufgabeId) verknuepfungen.push({ typ: 'aufgabe', id: aufgabeId });

      let result;
      if (kanal === 'email') {
        result = await betreuungAPI.sendEmail({
          an: form.an, betreff: form.betreff, inhalt: form.inhalt,
          klient_id: klientId, verknuepfungen
        });
      } else if (kanal === 'fax') {
        result = await betreuungAPI.sendFax({
          fax_nummer: form.fax_nummer, betreff: form.betreff, inhalt: form.inhalt,
          klient_id: klientId, verknuepfungen
        });
      } else if (kanal === 'ebo') {
        result = await betreuungAPI.sendEBO({
          empfaenger_safe_id: form.empfaenger_safe_id, betreff: form.betreff, inhalt: form.inhalt,
          klient_id: klientId, verknuepfungen
        });
      }

      if (result?.success) {
        if (aufgabeId) {
          await betreuungAPI.createAufgabenVerlauf(aufgabeId, {
            text: `${kanal.toUpperCase()} gesendet: ${form.betreff}`,
            typ: 'korrespondenz'
          });
        }
        toast.success(result.message || 'Gesendet!');
        navigate(-1);
      } else {
        toast.error(result?.error || 'Fehler');
      }
    } catch (e) { toast.error(e.message); }
    finally { setSending(false); }
  };

  const KANAELE = [
    { key: 'email', label: 'E-Mail', icon: Mail },
    { key: 'fax', label: 'Fax', icon: Printer },
    { key: 'ebo', label: 'EBO', icon: Monitor },
  ];

  return (
    <div className="space-y-6" data-testid="send-schriftstueck-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Send className="w-6 h-6" /> Schriftstück senden
          </h1>
          {aufgabeTitel && <p className="text-sm text-slate-500 mt-1">Aufgabe: {aufgabeTitel}</p>}
        </div>
      </div>

      <Card>
        <CardContent className="pt-6 space-y-4">
          <div className="flex gap-2">
            {KANAELE.map(k => (
              <button key={k.key} onClick={() => setKanal(k.key)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm transition-all ${
                  kanal === k.key ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
                data-testid={`kanal-${k.key}`}>
                <k.icon className="w-4 h-4" /> {k.label}
              </button>
            ))}
          </div>

          {kanal === 'email' && (
            <div><Label>An *</Label><Input value={form.an} onChange={e => setForm(p => ({ ...p, an: e.target.value }))} placeholder="empfaenger@example.com" data-testid="send-an" /></div>
          )}
          {kanal === 'fax' && (
            <div><Label>Faxnummer *</Label><Input value={form.fax_nummer} onChange={e => setForm(p => ({ ...p, fax_nummer: e.target.value }))} placeholder="+49..." data-testid="send-fax" /></div>
          )}
          {kanal === 'ebo' && (
            <div><Label>Empfänger SAFE-ID *</Label><Input value={form.empfaenger_safe_id} onChange={e => setForm(p => ({ ...p, empfaenger_safe_id: e.target.value }))} data-testid="send-ebo" /></div>
          )}

          <div><Label>Betreff</Label><Input value={form.betreff} onChange={e => setForm(p => ({ ...p, betreff: e.target.value }))} data-testid="send-betreff" /></div>
          <div><Label>Inhalt</Label><Textarea value={form.inhalt} onChange={e => setForm(p => ({ ...p, inhalt: e.target.value }))} rows={5} placeholder="Nachricht..." data-testid="send-inhalt" /></div>

          {aufgabeTitel && (
            <div className="bg-slate-50 rounded-lg p-3 text-xs text-slate-500">
              Wird automatisch verknüpft mit: <Badge variant="secondary" className="text-xs ml-1">{aufgabeTitel}</Badge>
              {massnahmeBezeichnung && <Badge variant="secondary" className="text-xs ml-1">{massnahmeBezeichnung}</Badge>}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={() => navigate(-1)}>Abbrechen</Button>
            <Button onClick={handleSend} disabled={sending} data-testid="send-submit">
              <Send className="w-4 h-4 mr-2" />{sending ? 'Wird gesendet...' : 'Senden'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
